import React, { Fragment } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { MyVideosTeaser } from './MyVideosTeaser';
import { useMyProgramTracking } from './use-my-program-tracking';
import { useRandomShowIds } from './use-random-show-ids';
import { times } from '../../../../../play-core/utils/times';
import { idToUrn } from '../../../../../play-core/utils/urn-helper';
import { loadLatestEpisodesByShowUrns } from '../../../api/load-latest-episodes-by-show-urns';
import { Swimlane } from '../../../components/Swimlane/Swimlane';
import { SwimlaneTitle } from '../../../components/Swimlane/SwimlaneTitle';
import { Swipeable } from '../../../components/Swipeable/Swipeable';
import { MediaTeaser } from '../../../components/Teaser/MediaTeaser/MediaTeaser';
import { MediaTeaserPlaceholder } from '../../../components/Teaser/MediaTeaser/MediaTeaserPlaceholder';
import { queryKeys } from '../../../config/use-query';
import { useVideoConfig } from '../../../config/VideoAppContext';
import { useAxios } from '../../../hooks/use-axios';
import { useHydrated } from '../../../hooks/use-hydrated';
import { usePersonalizationSettings, useShowState } from '../../../providers/DataProvider/DataContext';
import { useVideoHistory } from '../../../providers/VideoHistoryProvider/VideoHistoryContext';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const defaultValue = {
  data: undefined,
  error: null,
  isLoading: false
};
export const MyProgram = ({
  showMyVideoTeaser = true
}) => {
  const {
    businessUnit
  } = useVideoConfig();
  const axios = useAxios();
  const {
    t
  } = useTranslation();
  const {
    isActive: isPersonalizationActive
  } = usePersonalizationSettings();
  const {
    favoriteShowIds
  } = useShowState();
  const {
    showHistory,
    watchAgainIds
  } = useVideoHistory();
  const {
    usePreview
  } = useVideoConfig();
  const showFavoriteUrns = useRandomShowIds(favoriteShowIds).map(id => idToUrn(id, businessUnit, 'show:tv'));
  const showHistoryUrns = showHistory.map(entry => entry.urn).slice(0, 5);
  const hasFavorites = showFavoriteUrns.length > 0;
  const hasShows = showHistoryUrns.length > 0;
  const urns = hasFavorites ? showFavoriteUrns : showHistoryUrns;
  const queries = urns.map(urn => ({
    queryKey: [queryKeys.LatestEpisodesByShows(urn, 10), axios],
    queryFn: () => loadLatestEpisodesByShowUrns(axios, [urn], 10),
    enabled: hasFavorites || hasShows
  }));
  const {
    data: videos,
    error
  } = useQueries({
    queries
  }).reduce((previousValue, currentValue) => ({
    ...currentValue,
    data: previousValue?.data && currentValue?.data ? [...previousValue.data, ...currentValue.data] : previousValue?.data ?? currentValue?.data,
    isLoading: previousValue.isLoading || currentValue.isLoading
  }), defaultValue);
  const filteredVideos = videos?.filter(video => !watchAgainIds.includes(video.id)).sort((video1, video2) => new Date(video2.date).getTime() - new Date(video1.date).getTime());
  useMyProgramTracking(hasFavorites, filteredVideos);
  const isHydrated = useHydrated();
  const showSwimlane = !error && (isPersonalizationActive || !isHydrated);
  const PlaceholderSwipeable = /*#__PURE__*/_jsxs(Swipeable, {
    buttonPositionFactor: 9 / 16,
    isPersonalized: true,
    analyticsTitle: "MyProgram",
    children: [showMyVideoTeaser && /*#__PURE__*/_jsx(MyVideosTeaser, {}), times(10, index => /*#__PURE__*/_jsx(MediaTeaserPlaceholder, {}, index))]
  });
  const MyProgramSwipeable = /*#__PURE__*/_jsxs(Swipeable, {
    buttonPositionFactor: 9 / 16,
    isPersonalized: true,
    analyticsTitle: "MyProgram",
    children: [showMyVideoTeaser && /*#__PURE__*/_jsx(MyVideosTeaser, {}), filteredVideos?.slice(0, 30).map((video, index) => /*#__PURE__*/_jsx(MediaTeaser, {
      media: video,
      index: index,
      labelOrder: ['expiringSoon']
    }, video.id))]
  });
  return /*#__PURE__*/_jsx(Fragment, {
    children: showSwimlane && /*#__PURE__*/_jsxs(Swimlane, {
      children: [/*#__PURE__*/_jsx(SwimlaneTitle, {
        title: t('home.latestEpisodesByShowsTitle')
      }), usePreview || !filteredVideos?.length ? PlaceholderSwipeable : MyProgramSwipeable]
    })
  });
};